import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useAuth, useAppDispatch, useAppSelector } from '@verantos-dev/core-annotation-tool';
import { selectBatchId } from '@verantos-dev/feature-supervisor-queue';

import { useGetSupervisorWorkspaceQuery } from '../../api/supervisorApiSlice';
import { COMPLETED_STATUS_STR, SUPERVISOR_COMPLETED_STATUS_STR } from '../../constants';
import { useSupervisorMode } from '../../context/SupervisorModeContext';
import {
  selectSelectedNoteId,
  selectShowAnnotations,
  selectAnnotationIdsInEditMode,
  selectDirtyNewAnnotationConceptIds,
} from '../../state/selector';
import { setShowAnnotations, clearShowAnnotations } from '../../state/supervisorSlice';

interface UseReviewAnnotationsSidebarResult {
  selectedNoteId: string | null;
  showAnnotations: boolean;
  handleShowAnnotations: (flag: boolean) => void;
  showMarkTaskCompleteModal: boolean;
  setShowMarkTaskCompleteModal: (showMarkTaskCompleteModal: boolean) => void;
  disableMarkCompleteButton: boolean;
  hasPendingConflicts: boolean;
}

export const useReviewAnnotationsSidebar = (): UseReviewAnnotationsSidebarResult => {
  const dispatch = useAppDispatch();
  const { encounterId } = useParams();
  const { user } = useAuth();
  const { isReadOnly } = useSupervisorMode();
  const selectedNoteId = useAppSelector(selectSelectedNoteId);
  const showAnnotations = useAppSelector(selectShowAnnotations);
  const batchId = useAppSelector(selectBatchId);
  const { data: supervisorWorkspaceData } = useGetSupervisorWorkspaceQuery(
    { encounterId: encounterId as string, batchId: batchId as string },
    { skip: !user.isUser && !isReadOnly }
  );
  const [showMarkTaskCompleteModal, setShowMarkTaskCompleteModal] = useState<boolean>(false);
  const annotationIdsInEditMode = useAppSelector(selectAnnotationIdsInEditMode);
  const dirtyNewAnnotationConceptIds = useAppSelector(selectDirtyNewAnnotationConceptIds);

  const hasPendingConflicts = useMemo(() => {
    const annotationsReview = supervisorWorkspaceData?.data?.annotationsReview;
    if (!annotationsReview) return false;
    return annotationsReview.some((review) =>
      review.conflicts.some((conflict) => !conflict.resolved.annotations?.length)
    );
  }, [supervisorWorkspaceData]);

  const handleShowAnnotations = (flag: boolean) => {
    if (flag) {
      dispatch(setShowAnnotations());
    } else {
      dispatch(clearShowAnnotations());
    }
  };

  const isTaskCompleted = useMemo(() => {
    const status = supervisorWorkspaceData?.data?.task?.status || COMPLETED_STATUS_STR;

    return [SUPERVISOR_COMPLETED_STATUS_STR, COMPLETED_STATUS_STR].includes(status);
  }, [supervisorWorkspaceData]);

  const disableMarkCompleteButton = useMemo(() => {
    if (!supervisorWorkspaceData?.data?.annotationsReview) return true;

    let annotator1HasAnnotated = false;
    let annotator2HasAnnotated = false;

    for (const concept of supervisorWorkspaceData?.data?.annotationsReview ?? []) {
      // matching → both annotated
      if (concept.matchingAnnotation?.length) {
        annotator1HasAnnotated = true;
        annotator2HasAnnotated = true;
      }

      for (const conflict of concept.conflicts || []) {
        if (conflict.annotator1Annotation) annotator1HasAnnotated = true;
        if (conflict.annotator2Annotation) annotator2HasAnnotated = true;
      }

      if (annotator1HasAnnotated && annotator2HasAnnotated) break;
    }

    const bothAnnotated = annotator1HasAnnotated && annotator2HasAnnotated;
    const hasUnsavedWork =
      annotationIdsInEditMode.length > 0 || dirtyNewAnnotationConceptIds.length > 0;

    return !bothAnnotated || hasPendingConflicts || isTaskCompleted || hasUnsavedWork;
  }, [
    supervisorWorkspaceData?.data,
    hasPendingConflicts,
    isTaskCompleted,
    annotationIdsInEditMode,
    dirtyNewAnnotationConceptIds,
  ]);

  return {
    selectedNoteId,
    showAnnotations,
    handleShowAnnotations,
    showMarkTaskCompleteModal,
    setShowMarkTaskCompleteModal,
    disableMarkCompleteButton,
    hasPendingConflicts,
  };
};
